package org.teddapp.project.db

/**
 * Arithmétique de dates sur des chaînes ISO `YYYY-MM-DD`, sans dépendance
 * kotlinx-datetime (algorithme « civil_from_days » de Howard Hinnant).
 *
 * Utilisé par le moteur de récurrence Finance (triggers + listes périodiques) :
 * calcul des prochaines échéances et avance à la prochaine date future.
 */
object DateMath {

    /** Numéro de jour (epoch civil) depuis une date ISO. */
    fun isoToDayNumber(iso: String): Long {
        val y = iso.substring(0, 4).toInt()
        val m = iso.substring(5, 7).toInt()
        val d = iso.substring(8, 10).toInt()
        val ya = if (m <= 2) y - 1 else y
        val era = (if (ya >= 0) ya else ya - 399) / 400
        val yoe = (ya - era * 400).toLong()
        val mp = if (m > 2) m - 3 else m + 9
        val doy = (153 * mp + 2) / 5 + d - 1
        val doe = yoe * 365 + yoe / 4 - yoe / 100 + doy
        return era.toLong() * 146097L + doe - 719468L
    }

    /** Date ISO depuis un numéro de jour civil. */
    fun dayNumberToIso(days: Long): String {
        val z = days + 719468L
        val era = (if (z >= 0L) z else z - 146096L) / 146097L
        val doe = z - era * 146097L
        val yoe = (doe - doe / 1460L + doe / 36524L - doe / 146096L) / 365L
        val y = yoe + era * 400L
        val doy = doe - (365L * yoe + yoe / 4L - yoe / 100L)
        val mp = (5L * doy + 2L) / 153L
        val d = doy - (153L * mp + 2L) / 5L + 1L
        val m = if (mp < 10L) mp + 3L else mp - 9L
        val yr = if (m <= 2L) y + 1L else y
        return iso(yr.toInt(), m.toInt(), d.toInt())
    }

    fun isoAddDays(iso: String, days: Int): String = dayNumberToIso(isoToDayNumber(iso) + days)

    /**
     * Ajoute [months] mois en bornant le jour au dernier jour du mois cible
     * (31 janvier + 1 mois → 28/29 février).
     */
    fun isoAddMonths(iso: String, months: Int): String {
        val y = iso.substring(0, 4).toInt()
        val m = iso.substring(5, 7).toInt()
        val d = iso.substring(8, 10).toInt()
        val total = (y * 12 + (m - 1)) + months
        val ny = total / 12
        val nm = total % 12 + 1
        val nd = d.coerceAtMost(daysInMonth(ny, nm))
        return iso(ny, nm, nd)
    }

    fun isoAddYears(iso: String, years: Int): String = isoAddMonths(iso, years * 12)

    /**
     * Compare deux dates ISO (négatif si a<b, 0 si égales, positif si a>b).
     * Le format ISO étant lexicographiquement ordonné, une simple comparaison de
     * chaînes suffit ; helper sémantique.
     */
    fun isoCompare(a: String, b: String): Int = a.compareTo(b)

    /**
     * Avance [start] d'une période [frequency] de façon répétée jusqu'à obtenir
     * une date strictement postérieure à [after]. Sert au « snap » d'un trigger
     * qui a manqué plusieurs échéances : on ne rejoue qu'une fois, puis on cale la
     * prochaine occurrence dans le futur. Garde-fou de 2000 itérations.
     */
    fun nextAfter(start: String, after: String, frequency: String): String {
        var d = step(start, frequency)
        var guard = 0
        while (d <= after && guard < 2000) {
            d = step(d, frequency)
            guard++
        }
        return d
    }

    /** Une étape de la fréquence donnée à partir de [iso]. */
    fun step(iso: String, frequency: String): String = when (frequency) {
        FREQ_DAILY -> isoAddDays(iso, 1)
        FREQ_WEEKLY -> isoAddDays(iso, 7)
        FREQ_BIWEEKLY -> isoAddDays(iso, 14)
        FREQ_MONTHLY -> isoAddMonths(iso, 1)
        FREQ_QUARTERLY -> isoAddMonths(iso, 3)
        FREQ_YEARLY -> isoAddYears(iso, 1)
        else -> isoAddMonths(iso, 1)
    }

    private fun daysInMonth(year: Int, month: Int): Int = when (month) {
        1, 3, 5, 7, 8, 10, 12 -> 31
        4, 6, 9, 11 -> 30
        2 -> if (isLeap(year)) 29 else 28
        else -> 30
    }

    private fun isLeap(y: Int): Boolean = (y % 4 == 0 && y % 100 != 0) || y % 400 == 0

    private fun iso(y: Int, m: Int, d: Int): String =
        "${y.toString().padStart(4, '0')}-${m.toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}"

    const val FREQ_DAILY = "daily"
    const val FREQ_WEEKLY = "weekly"
    const val FREQ_BIWEEKLY = "biweekly"
    const val FREQ_MONTHLY = "monthly"
    const val FREQ_QUARTERLY = "quarterly"
    const val FREQ_YEARLY = "yearly"

    /** Fréquences proposées dans l'UI (clé → libellé court FR). */
    val FREQUENCIES: List<Pair<String, String>> = listOf(
        FREQ_DAILY to "Quotidien",
        FREQ_WEEKLY to "Hebdo",
        FREQ_BIWEEKLY to "2 sem.",
        FREQ_MONTHLY to "Mensuel",
        FREQ_QUARTERLY to "Trimestre",
        FREQ_YEARLY to "Annuel",
    )

    const val RECURRENCE_ONCE = "once"
}
